import { isNotNil, isNumber, isString } from '~/utilities/type-guards';

export type TimelineLocation = 'before' | 'end';

export type Plugin = {
  on?: boolean;
};

export type EnabledPlugin = AnyPlugin & {
  on: true;
};

export type PluginNames = keyof PlayerPlugins;
export type ToggleablePluginNames = Exclude<
  PluginNames,
  'thumbnailTextOverlay-v2' | 'videoThumbnail'
>;

export type PlayerPlugins = {
  audioChapters: AudioChaptersPlugin;
  chapters: ChaptersPlugin;
  'midrollLink-v1': AnnotationsPlugin;
  'requireEmail-v1': TurnstilePlugin;
  'postRoll-v1': CTAPlugin;
  'thumbnailTextOverlay-v2': ThumbnailTextOverlayPlugin;
  hubspotForm: HubSpotFormPlugin;
  marketoForm: MarketoFormPlugin;
  pardotForm: PardotFormPlugin;
  videoThumbnail: VideoThumbnailPlugin;
  share: SharePlugin;
  passwordProtectedVideo: PasswordProtectPlugin;
  'captions-v1': CaptionsPlugin;
  captions: CaptionsPlugin;
  extendedAudioDescription: ExtendedAudioDescriptionPlugin;
};

export type AnyPlugin =
  | AnnotationsPlugin
  | CaptionsPlugin
  | ChaptersPlugin
  | CTAPlugin
  | PasswordProtectPlugin
  | SharePlugin
  | ThumbnailTextOverlayPlugin
  | TurnstilePlugin
  | VideoThumbnailPlugin;

export type CaptionsPlugin = Plugin & {
  onByDefault?: boolean;
};
export type ExtendedAudioDescriptionPlugin = Plugin;

export type PasswordProtectPlugin = Plugin & {
  challenge: string;
};

export type TurnstilePlugin = Plugin & {
  allowSkip: boolean;
  askName: boolean;
  async: boolean;
  bottomText: string;
  conversionOpportunityKey: string;
  list?: number | string;
  is_v3_list_id?: boolean;
  persistentTurnstile: boolean;
  provider?: string;
  submitButtonText?: string;
  time: TimelineLocation | number;
  topText: string;
};

export type CTAPlugin = Plugin & {
  autoSize: boolean;
  altText?: string;
  conversionOpportunityKey: string;
  // todo: confirm these types match
  ctaType: 'image' | 'raw' | 'text';
  link: string;
  rewatch: boolean;
  style: object;
  text: string;
  time: TimelineLocation | number;
  image?: string;
  raw?: string;
};

export type Chapter = {
  id: string;
  title: string;
  time: number;
  deleted: boolean;
};

export type ChaptersPlugin = Plugin & {
  chapterList?: Chapter[];
};

export type AudioChaptersPlugin = ChaptersPlugin & {
  visibleOnLoad: boolean;
};

export type Annotation = {
  active: boolean;
  conversionOpportunityKey?: string;
  duration: number;
  name: string;
  text: string;
  time: number;
  url: string;
  theme?: 'dark' | 'light';
  position?: 'left' | 'right';
};

export type AnnotationsPlugin = Plugin & {
  links?: Annotation[];
};

export type VideoThumbnailPlugin = Plugin & {
  clickToPlayButton?: boolean;
  fitStrategy?: 'contain' | 'cover' | 'fill' | 'none';
  hashedId: string;
  priorityMode?: boolean;
  trimEnd: number;
  trimStart: number;
  clickForSound?: {
    on?: boolean;
    showText?: boolean;
  };
};

export type SharePlugin = Plugin & {
  channels: string; // dash separated list of available sharing channels, e.g. 'embed-email-download-facebook-twitter-linkedIn'
  pageTitle: string;
  pageUrl: string;
  tweetText: string;
  overrideUrl: boolean;
  on: boolean;
  conversionOpportunityKey?: string;
  downloadType?: 'all_qualities' | 'hd_mp4' | 'original' | 'sd_mp4';
};

export type ThumbnailTextOverlayPlugin = Plugin & {
  text: string;
  includeTextOverlay: boolean;
  textOverlayImageUrl?: string;
};

export type HubSpotFormPlugin = Plugin & {
  hubspotFormId: string;
  hubspotId: number;
  hubspotFormSeconds: number;
  hubspotFormIsSkippable: boolean;
  hubspotFormEnabled: boolean;
  hubspotFormHeaderText: string;
};

export type MarketoFormPlugin = Plugin & {
  marketoBaseUrl: string;
  marketoFormEnabled: boolean;
  marketoFormHeaderText: string;
  marketoFormId: number | string;
  marketoFormIsSkippable: boolean;
  marketoFormSeconds: number;
  marketoRestSubdomain: string;
  marketoDesignStudioUrl?: string;
};

export type PardotFormPlugin = Plugin & {
  pardotFormUrl: string | null;
  pardotFormEnabled: boolean;
  pardotFormHeaderText: string;
  pardotFormId: number | string;
  pardotFormIsSkippable: boolean;
  pardotFormSeconds: number;
};

export type ControlsEmbedOptions = {
  autoPlay: boolean;
  controlsVisibleOnLoad: boolean;
  volumeControl: boolean;
  fullscreenButton: boolean;
  bpbTime: boolean;
  clickForSound?: {
    showText: boolean;
  };
  playButton?: boolean;
  smallPlayButton?: boolean;
  playbar?: boolean;
  settingsControl?: boolean;
  playbackRateControl?: boolean;
  qualityControl?: boolean;
  spherical?: boolean;
  silentAutoPlay?: boolean | 'allow';
  muted?: boolean;
  endVideoBehavior?: 'default' | 'loop' | 'reset';
};

export type EphemeralAnnotationOptions = {
  name?: string;
  shouldDelete?: boolean;
};

export type EphemeralAudioDescriptionOptions = {
  audioTracks: unknown[] | 'none';
  playerTracks: unknown[] | 'none';
};

export type EphemeralExtendedAudioDescriptionOptions = {
  tracks: unknown[] | 'none';
};

export type GradientColor = [string, number];

export type PlayerColorGradient = {
  on: boolean;
  colors: GradientColor[];
};

export type EmbedOptions = ControlsEmbedOptions & {
  audioDescriptionControl?: boolean;
  branding?: boolean;
  customerLogoPlacement?: string;
  customerLogoSizePercent?: number;
  customerLogoTargetUrl?: string;
  customerLogoImageUrl?: string;
  controlsVisibleOnLoad?: boolean;
  ephemeral?: {
    passwordProtect?: {
      savedPassword?: string;
    };
    ui?: {
      annotations?: { isOpen: boolean };
      appearance?: { isOpen: boolean };
      audioChapters?: { isOpen: boolean };
      audioDescription?: { isOpen: boolean };
      audioShare?: { isOpen: boolean };
      callToAction?: { isOpen: boolean };
      chapters?: { isOpen: boolean };
      controls?: { isOpen: boolean };
      hubspotForm?: { isOpen: boolean };
      image?: { isOpen: boolean };
      marketoForm?: { isOpen: boolean };
      pardotForm?: { isOpen: boolean };
      passwordProtect?: { isOpen: boolean };
      share?: { isOpen: boolean };
      thumbnail?: { isOpen: boolean };
      transcript?: { isOpen: boolean };
      turnstile?: { isOpen: boolean };
    };
    annotation?: EphemeralAnnotationOptions;
    thumbnail?: {
      isUploadingThumbnailForVideoThumbnail?: boolean;
    };
    audioDescriptions?: EphemeralAudioDescriptionOptions;
    extendedAudioDescriptions?: EphemeralExtendedAudioDescriptionOptions;
    localizations?: unknown[];
  };
  playerColor: string;
  playerColorGradient?: PlayerColorGradient;
  plugin?: Partial<PlayerPlugins>;
  roundedPlayer?: number;
  showCustomerLogo: boolean;
  stillUrl?: string;
  time?: number;
  thumbnailAltText?: string;
  transcriptEnabled?: boolean;
  unalteredStillImageAsset?: Partial<{
    url: string;
    width: number;
    height: number;
  }>;
  private?: {
    password_protect_on?: boolean;
  };
  encrypted?: {
    password_protect_password?:
      | string
      | {
          current: string;
          previous: string;
        };
  };
};

export const isEmbedOptions = (embedOptions: unknown): embedOptions is EmbedOptions => {
  if (typeof embedOptions !== 'object' || embedOptions === null) {
    return false;
  }

  const embedOptionsAsEmbedOptions = embedOptions as EmbedOptions;

  // TODO: figure out which options are actually guaranteed to exist when embed
  // options are retrieved from the server. Maybe it's none?

  // the player color could be a number or string after casting... specifically it could be a number if all the numerals
  // in the hexcode are digits. We will ensure it gets casted back to a string, but we need to check if it's a valid string or number first.
  return (
    isString(embedOptionsAsEmbedOptions.playerColor) ||
    isNumber(embedOptionsAsEmbedOptions.playerColor)
  );
};

export type EmbedOptionsWithNonNullPlugin = EmbedOptions & {
  plugin: Partial<PlayerPlugins>;
};

export const isEmbedOptionsWithNonNullPlugin = (
  embedOptions: unknown,
): embedOptions is EmbedOptionsWithNonNullPlugin => {
  if (!isEmbedOptions(embedOptions)) {
    return false;
  }

  return isNotNil(embedOptions.plugin);
};

export type AudioDescriptionType = 'extended' | 'none' | 'standard';
